import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Quote, Link as LinkIcon } from "lucide-react";
import { Card } from "./ui/card";
import { getCuratedTestimonials } from "../lib/dynamicContent";
import { getTranslation } from "../lib/translations";

interface TestimonialsSectionProps {
  testimonials: any[];
  lang: string;
  t: any;
}

interface TestimonialCardProps {
  item: any;
  lang: string;
}

function TestimonialCard({ item, lang }: TestimonialCardProps) {
  const authorName = getTranslation(item.author, lang);
  const quoteText = (getTranslation(item.quote, lang) || "") as string;
  const paragraphs = quoteText.split(/\r?\n/).filter((p) => p.trim() !== "");

  return (
    <Card className="bg-card text-card-foreground p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center md:items-start relative border border-border/60 shadow-sm min-h-[180px] w-full text-left">
      {/* Left Column: Avatar */}
      <div className="flex-shrink-0 relative">
        {item.avatar_url ? (
          <img
            src={item.avatar_url}
            alt={authorName}
            className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border border-border/80 shadow-inner"
          />
        ) : (
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-brand-primary/5 dark:bg-brand-primary/10 border border-brand-primary/15 flex items-center justify-center text-brand-primary font-extrabold text-2xl shadow-inner uppercase">
            {authorName.substring(0, 2)}
          </div>
        )}
        <div className="absolute -bottom-2 -right-2 bg-brand-primary text-white p-1.5 rounded-full shadow-md">
          <Quote className="h-4.5 w-4.5 transform rotate-180" />
        </div>
      </div>

      {/* Right Column: Content */}
      <div className="flex-1 w-full flex flex-col justify-between">
        <div>
          <h4 className="text-xl font-bold text-foreground">
            {authorName}
          </h4>
          <p className="text-sm text-brand-secondary font-semibold mt-1">
            {getTranslation(item.role, lang)} @ {getTranslation(item.company, lang)}
          </p>
          <div className="mt-4 space-y-3">
            {paragraphs.map((para, pIdx) => (
              <p
                key={pIdx}
                className="testimonial-quote text-base text-muted-foreground leading-relaxed font-sans italic break-words"
              >
                {pIdx === 0 && `"`}
                {para}
                {pIdx === paragraphs.length - 1 && `"`}
              </p>
            ))}
          </div>
          {item.links && item.links.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border/20">
              {item.links.slice(0, 5).map((link: any, linkIdx: number) => (
                <a
                  key={linkIdx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors border border-brand-primary/20 hover:border-brand-primary/40 rounded-full px-3 py-1 bg-brand-primary/5 hover:bg-brand-primary/10 shadow-sm"
                >
                  <LinkIcon className="h-3 w-3" />
                  <span>{lang === "tr" ? link.name_tr || link.name_en : link.name_en || link.name_tr}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

interface PaginationDotsProps {
  count: number;
  activeIndex: number;
  onSelect: (idx: number) => void;
}

function PaginationDots({ count, activeIndex, onSelect }: PaginationDotsProps) {
  return (
    <div className="flex justify-center gap-2 pt-2">
      {Array.from({ length: count }).map((_, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(idx)}
          className="w-8 h-8 flex items-center justify-center cursor-pointer focus:outline-none"
          aria-label={`Go to slide ${idx + 1}`}
        >
          <span
            className={`h-2.5 rounded-full transition-all duration-300 ${idx === activeIndex
              ? "w-8 bg-brand-primary"
              : "w-2.5 bg-muted-foreground/35 hover:bg-muted-foreground/50"
              }`}
          />
        </button>
      ))}
    </div>
  );
}

interface NavigationArrowsProps {
  onPrev: () => void;
  onNext: () => void;
}

function NavigationArrows({ onPrev, onNext }: NavigationArrowsProps) {
  return (
    <>
      <button
        onClick={onPrev}
        className="absolute left-0 md:left-2 top-1/2 -translate-y-1/2 p-2 rounded-full border border-border/60 bg-background/80 hover:bg-background text-foreground shadow-md hover:scale-105 transition-all duration-200 cursor-pointer opacity-80 hover:opacity-100 z-20"
        aria-label="Previous testimonial"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={onNext}
        className="absolute right-0 md:right-2 top-1/2 -translate-y-1/2 p-2 rounded-full border border-border/60 bg-background/80 hover:bg-background text-foreground shadow-md hover:scale-105 transition-all duration-200 cursor-pointer opacity-80 hover:opacity-100 z-20"
        aria-label="Next testimonial"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </>
  );
}

export function TestimonialsSection({ testimonials, lang, t }: TestimonialsSectionProps) {
  const curated = getCuratedTestimonials(testimonials).sort((a, b) => a.sort_order - b.sort_order);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [activeHeight, setActiveHeight] = useState<number | null>(null);
  const pauseTimerRef = useRef<any>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (curated.length <= 1) return;

    const interval = setInterval(() => {
      if (!isPaused) {
        setActiveIndex((prev) => (prev + 1) % curated.length);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, curated.length]);

  useEffect(() => {
    const updateHeight = () => {
      const activeNode = slideRefs.current[activeIndex];
      if (activeNode) {
        setActiveHeight(activeNode.offsetHeight);
      }
    };

    updateHeight();

    const activeNode = slideRefs.current[activeIndex];
    if (!activeNode) return;

    const observer = new ResizeObserver(() => {
      updateHeight();
    });
    observer.observe(activeNode);

    return () => {
      observer.disconnect();
    };
  }, [activeIndex, curated]);

  const triggerPause = () => {
    setIsPaused(true);
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
    }
    pauseTimerRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 15000);
  };

  const handlePrev = () => { triggerPause(); setActiveIndex((prev) => (prev - 1 + curated.length) % curated.length); };
  const handleNext = () => { triggerPause(); setActiveIndex((prev) => (prev + 1) % curated.length); };
  const handleSelect = (idx: number) => { triggerPause(); setActiveIndex(idx); };

  if (curated.length === 0) return null;

  return (
    <section className="space-y-6 pt-6 border-t border-border/40">
      <div className="border-b border-border/40 pb-4 text-left">
        <h3 className="text-2xl font-extrabold tracking-tight text-foreground">{t.testimonialsTitle}</h3>
        <p className="text-sm text-muted-foreground mt-1">{t.testimonialsSubtitle}</p>
      </div>

      <div className="relative w-full px-12 md:px-16">
        <div
          style={{ height: activeHeight ? `${activeHeight}px` : "auto" }}
          className="relative group w-full overflow-hidden transition-[height] duration-300 ease-in-out"
        >
          {/* Carousel Track */}
          <div className="w-full transition-all duration-500 ease-in-out flex items-start">
            {curated.map((item, idx) => {
              return (
                <div
                  key={item.id}
                  ref={(el) => {
                    slideRefs.current[idx] = el;
                  }}
                  style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                  className="w-full shrink-0 transition-transform duration-500 ease-in-out px-1 py-2"
                >
                  <TestimonialCard item={item} lang={lang} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Navigation Arrows */}
        {curated.length > 1 && (
          <NavigationArrows onPrev={handlePrev} onNext={handleNext} />
        )}
      </div>

      {/* Pagination dots */}
      {curated.length > 1 && (
        <PaginationDots
          count={curated.length}
          activeIndex={activeIndex}
          onSelect={handleSelect}
        />
      )}
    </section>
  );
}
